import { Dispatch, SetStateAction, useId, useState } from "react";
import { getDownloadURL, ref, uploadBytes, uploadBytesResumable } from "firebase/storage";
import { storage } from "../../../../../firebase";
import { useAuth } from "../../../../../context/authProvider";
import { compressImage, addWatermark } from "./functions";
import AdminButton from "../../../../core/admin/adminButton";
import AdminSpinner from "../../../../core/admin/adminSpinner";
import { PlusLineIcon } from "../../../../../img/svg/adminIcons";

export default function FileInput({ watchId, isDisabled = true, saveImgUrls, loading, setLoading }: { watchId: string, isDisabled?: boolean, saveImgUrls: (newImages: { imgUrl: string, cImgUrl: string }[]) => Promise<any>, loading: boolean, setLoading: Dispatch<SetStateAction<boolean>> }) {
    const { userId } = useAuth();
    const inputId = useId();
    const [picText, setPicText] = useState("");
    const [picValue, setPicValue] = useState<File[]>([]);
    const [totalPicSize, setTotalPicSize] = useState(0);
    const [hasError, setHasError] = useState(false);

    const sendImage = async (e: any) => {
        e?.preventDefault?.();
        try {
            setLoading(true);
            setHasError(false);

            if (picValue.length === 0) {
                setLoading(false);
                return null;
            }

            // Validar se algum arquivo é maior que 5MB (opcional, já que mostramos no UI)
            const oversized = picValue.some(f => (f.size / 1024000) > 5);
            if (oversized) {
                setLoading(false);
                setHasError(true);
                return null;
            }

            const uploadedImages: { imgUrl: string, cImgUrl: string }[] = [];

            for (const file of picValue) {
                const compressedImage = await compressImage(file);
                const watermarkedOriginalPromise = addWatermark(file);
                const watermarkedCompressedPromise = addWatermark(compressedImage);

                const [watermarkedOriginal, watermarkedCompressed] = await Promise.all([
                    watermarkedOriginalPromise,
                    watermarkedCompressedPromise,
                ]);

                // Usar timestamp para evitar colisões de nome se múltiplos arquivos tiverem o mesmo nome
                const timestamp = Date.now();
                const fileName = `${timestamp}_${file.name}`;

                const storageRef = ref(storage, `watches/${userId}/${watchId}/${fileName}`);
                const uploadTask = await uploadBytesResumable(storageRef, watermarkedOriginal);
                const imgUrl = await getDownloadURL(uploadTask.ref);

                const cStorageRef = ref(storage, `watches/${userId}/${watchId}/cmprsd_${fileName}`);
                const cUploadTask = await uploadBytes(cStorageRef, watermarkedCompressed);
                const cImgUrl = await getDownloadURL(cUploadTask.ref);

                uploadedImages.push({ imgUrl, cImgUrl });
            }

            await saveImgUrls(uploadedImages);
            onCancel();
        } catch (error) {
            console.log(error);
            setHasError(true);
            setLoading(false);
        }
    }

    const onChoose = (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const files = e.target.files;
            if (!files || files.length === 0) {
                return null;
            }
            
            const filesArray = Array.from(files);
            const size = filesArray.reduce((acc, f) => acc + (f.size / 1024000), 0);
            
            setTotalPicSize(size);
            setPicValue(filesArray);
            
            if (filesArray.length === 1) {
                setPicText(filesArray[0].name);
            } else {
                setPicText(`${filesArray.length} arquivos selecionados`);
            }
        } catch (error) {
            console.log(error);
        }
    }

    const onCancel = () => {
        setPicValue([]);
        setPicText("");
        setTotalPicSize(0);
    }

    return (
        <div className="w-32">
            <div className="flex h-32 w-32 items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-white">
                {
                    loading ?
                        <span className="flex flex-col items-center gap-2 text-gray-500 text-xs font-semibold">
                            <AdminSpinner />
                            Enviando...
                        </span>
                        : picValue.length === 0 ?
                            <label title="Adicionar" htmlFor={inputId} className={`flex h-full w-full flex-col items-center justify-center gap-1 text-xs font-semibold text-gray-500 transition ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:text-gray-900 hover:bg-gray-50"}`}>
                                <PlusLineIcon width="24px" />
                                Adicionar
                                <input id={inputId} type="file" accept="image/*" multiple disabled={isDisabled} className="hidden" onChange={e => onChoose(e)} />
                            </label>
                            :
                            <div className="flex w-full flex-col gap-2 px-2">
                                <AdminButton size="sm" disabled={isDisabled || loading || picValue.length === 0} onClick={() => sendImage(null)} fullWidth>Enviar</AdminButton>
                                <AdminButton size="sm" variant="secondary" onClick={onCancel} fullWidth>Cancelar</AdminButton>
                            </div>
                }
            </div>
            <p className={`${isDisabled ? "text-gray-300" : (picValue.some(f => (f.size / 1024000) > 5)) ? "text-red-600 font-semibold" : "text-gray-600"} text-center text-xs mt-1 break-all`}>
                {picText} {totalPicSize ? `(${totalPicSize.toFixed(2)}MB)` : null}
            </p>
            {
                hasError ?
                    <p className="text-red-600 text-center text-xs">Erro no envio! Verifique o tamanho das imagens.</p>
                    : null
            }

        </div>
    )
}
